import { translate } from '@thoughtspot/i18n';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { getSessionInfo, getUserPersona, UserPersona, } from '@thoughtspot/session-service';
import { AgentId, FunctionId, IndustryId, MilestoneAgentName, MilestoneStatus, UseCaseId, } from './types';
export const DEFAULT_FUNCTION_ID = FunctionId.SALES_ACCOUNT_MANAGEMENT;
export const DEFAULT_INDUSTRY_ID = IndustryId.RETAIL_CPG;
export const SPARKFLOW_BASE_PATHS = {
    ONBOARDING: '/sparkflow/onboarding',
    PINBOARD: '/sparkflow/pinboard',
};
export var UseCaseMode;
(function (UseCaseMode) {
    UseCaseMode["SEARCH"] = "Search";
    UseCaseMode["RESEARCH"] = "Research";
})(UseCaseMode || (UseCaseMode = {}));
export const OTHER_FUNCTION_ID = 'other-function';
export const OTHER_INDUSTRY_ID = 'other-industry';
export const ONBOARDING_IMAGE_SRC = '/node_modules/@thoughtspot/radiant-styles/public/img/sparkflow/sparkflow-onboarding.svg';
export const THOUGHTSPOT_LOGO_SRC = '/node_modules/@thoughtspot/radiant-styles/public/img/ThoughtSpot_icon_black.svg';
export const FUNCTION_TO_PERSONA_MAP = {
    [FunctionId.DATA_ANALYTICS]: UserPersona.ANALYST_PERSONA,
    [FunctionId.PRODUCT_MANAGEMENT]: UserPersona.BUSINESS_PERSONA,
    [FunctionId.SALES_ACCOUNT_MANAGEMENT]: UserPersona.BUSINESS_PERSONA,
    [FunctionId.MARKETING_GROWTH]: UserPersona.BUSINESS_PERSONA,
    [FunctionId.FINANCE_ACCOUNTING]: UserPersona.BUSINESS_PERSONA,
    [FunctionId.EXECUTIVE_LEADERSHIP]: UserPersona.BUSINESS_PERSONA,
    [FunctionId.SOFTWARE_ENGINEERING]: UserPersona.DEVELOPER_PERSONA,
    [OTHER_FUNCTION_ID]: UserPersona.BUSINESS_PERSONA,
};
export const PERSONA_ORDER_MAP = {
    [UserPersona.BUSINESS_PERSONA]: [
        AgentId.ASK_QUESTIONS,
        AgentId.BUILD_DASHBOARD,
        AgentId.EMBED_INSIGHTS,
    ],
    [UserPersona.ANALYST_PERSONA]: [
        AgentId.BUILD_DASHBOARD,
        AgentId.ASK_QUESTIONS,
        AgentId.EMBED_INSIGHTS,
    ],
    [UserPersona.DEVELOPER_PERSONA]: [
        AgentId.EMBED_INSIGHTS,
        AgentId.ASK_QUESTIONS,
        AgentId.BUILD_DASHBOARD,
    ],
};
export const getAgentOptions = (isSparkflowUsecaseBasedOnboardingEnabled = false) => {
    return [
        {
            id: AgentId.ASK_QUESTIONS,
            title: translate('sparkflow.agentSelection.askQuestions.title'),
            description: isSparkflowUsecaseBasedOnboardingEnabled
                ? translate('sparkflow.useCase.askQuestions.description')
                : translate('sparkflow.agentSelection.askQuestions.description'),
            icon: IconID.SPOTTER_NEW,
            question: translate('sparkflow.spotter.preFilledQuestion'),
        },
        {
            id: AgentId.BUILD_DASHBOARD,
            title: translate('sparkflow.agentSelection.buildDashboard.title'),
            description: translate('sparkflow.agentSelection.buildDashboard.description'),
            icon: IconID.SPOTTER_VIZ_ONBOARDING,
            question: '',
        },
        {
            id: AgentId.EMBED_INSIGHTS,
            title: translate('sparkflow.agentSelection.embedInsights.title'),
            description: translate('sparkflow.agentSelection.embedInsights.description'),
            icon: IconID.SPOTTER_CODE,
            question: '',
        },
    ];
};
export const getOrderedAgentOptions = (isSparkflowUsecaseBasedOnboardingEnabled) => {
    const userPersona = getUserPersona() || UserPersona.BUSINESS_PERSONA;
    const allOptions = getAgentOptions(isSparkflowUsecaseBasedOnboardingEnabled);
    const orderArray = PERSONA_ORDER_MAP[userPersona];
    const optionsMap = new Map(allOptions.map(option => [option.id, option]));
    const orderedList = orderArray
        ?.map(id => optionsMap.get(id))
        ?.filter(Boolean);
    const list = orderedList?.length > 0 ? orderedList : allOptions;
    if (isSparkflowUsecaseBasedOnboardingEnabled) {
        return [list[1], list[0], list[2]];
    }
    return list;
};
export const getFunctionOptions = () => {
    return [
        {
            id: FunctionId.DATA_ANALYTICS,
            label: translate('sparkflow.function.dataAnalyst'),
        },
        {
            id: FunctionId.PRODUCT_MANAGEMENT,
            label: translate('sparkflow.function.productManagement'),
        },
        {
            id: FunctionId.SALES_ACCOUNT_MANAGEMENT,
            label: translate('sparkflow.function.salesAccountManagement'),
        },
        {
            id: FunctionId.MARKETING_GROWTH,
            label: translate('sparkflow.function.marketingGrowth'),
        },
        {
            id: FunctionId.FINANCE_ACCOUNTING,
            label: translate('sparkflow.function.financeAccounting'),
        },
        {
            id: FunctionId.EXECUTIVE_LEADERSHIP,
            label: translate('sparkflow.function.executiveLeadership'),
        },
        {
            id: FunctionId.SOFTWARE_ENGINEERING,
            label: translate('sparkflow.function.softwareEngineer'),
        },
        {
            id: OTHER_FUNCTION_ID,
            label: translate('sparkflow.function.other'),
        },
    ];
};
export const getIndustryOptions = () => {
    return [
        {
            id: IndustryId.RETAIL_CPG,
            label: translate('sparkflow.industry.cpg'),
        },
        {
            id: IndustryId.FINANCIAL_SERVICES,
            label: translate('sparkflow.industry.financialServices'),
        },
        {
            id: IndustryId.SOFTWARE_TECHNOLOGY,
            label: translate('sparkflow.industry.softwareTechnology'),
        },
        {
            id: IndustryId.HEALTHCARE_LIFE_SCIENCES,
            label: translate('sparkflow.industry.healthcareLifeSciences'),
        },
        {
            id: IndustryId.MANUFACTURING_LOGISTICS,
            label: translate('sparkflow.industry.manufacturingLogistics'),
        },
        {
            id: IndustryId.MEDIA_TELECOM,
            label: translate('sparkflow.industry.mediaTelecom'),
        },
        {
            id: IndustryId.HOSPITALITY_TRANSPORTATION,
            label: translate('sparkflow.industry.hospitalityTransportation'),
        },
        {
            id: OTHER_INDUSTRY_ID,
            label: translate('sparkflow.industry.other'),
        },
    ];
};
export const getUseCaseQuestions = (functionId, industryId, isDeepResearch) => {
    const USE_CASE_QUESTIONS_MAP = {
        [`${FunctionId.SALES_ACCOUNT_MANAGEMENT}__${IndustryId.FINANCIAL_SERVICES}`]: {
            normal: [
                {
                    id: UseCaseId.WIN_RATE_DROP,
                    title: translate('sparkflow.useCase.salesAccountManagement.financialServices.winRateDrop.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.financialServices.winRateDrop.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.financialServices.winRateDrop.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DEALS_DRAGGING,
                    title: translate('sparkflow.useCase.salesAccountManagement.financialServices.dealsDragging.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.financialServices.dealsDragging.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.financialServices.dealsDragging.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.RENEWAL_RISK,
                    title: translate('sparkflow.useCase.salesAccountManagement.financialServices.renewalRisk.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.financialServices.renewalRisk.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.financialServices.renewalRisk.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.WIN_RATE_COLLAPSE,
                    title: translate('sparkflow.useCase.salesAccountManagement.financialServices.winRateCollapse.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.financialServices.winRateCollapse.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.financialServices.winRateCollapse.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.PIPELINE_LEAK,
                    title: translate('sparkflow.useCase.salesAccountManagement.financialServices.pipelineLeak.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.financialServices.pipelineLeak.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.financialServices.pipelineLeak.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.QUOTA_AND_CHURN,
                    title: translate('sparkflow.useCase.salesAccountManagement.financialServices.quotaAndChurn.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.financialServices.quotaAndChurn.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.financialServices.quotaAndChurn.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.MARKETING_GROWTH}__${IndustryId.FINANCIAL_SERVICES}`]: {
            normal: [
                {
                    id: UseCaseId.CAC_SPIKE,
                    title: translate('sparkflow.useCase.marketingGrowth.financialServices.cacSpike.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.financialServices.cacSpike.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.financialServices.cacSpike.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.CONVERSION_FALL,
                    title: translate('sparkflow.useCase.marketingGrowth.financialServices.conversionFall.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.financialServices.conversionFall.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.financialServices.conversionFall.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.ROAS_EROSION,
                    title: translate('sparkflow.useCase.marketingGrowth.financialServices.roasErosion.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.financialServices.roasErosion.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.financialServices.roasErosion.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.CAC_BREAKDOWN,
                    title: translate('sparkflow.useCase.marketingGrowth.financialServices.cacBreakdown.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.financialServices.cacBreakdown.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.financialServices.cacBreakdown.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.MQL_WASTE,
                    title: translate('sparkflow.useCase.marketingGrowth.financialServices.mqlWaste.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.financialServices.mqlWaste.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.financialServices.mqlWaste.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.REGIONAL_ROAS_GAP,
                    title: translate('sparkflow.useCase.marketingGrowth.financialServices.regionalRoasGap.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.financialServices.regionalRoasGap.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.financialServices.regionalRoasGap.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.PRODUCT_MANAGEMENT}__${IndustryId.FINANCIAL_SERVICES}`]: {
            normal: [
                {
                    id: UseCaseId.ADOPTION_DECLINE,
                    title: translate('sparkflow.useCase.productManagement.financialServices.adoptionDecline.title'),
                    description: translate('sparkflow.useCase.productManagement.financialServices.adoptionDecline.description'),
                    question: translate('sparkflow.useCase.productManagement.financialServices.adoptionDecline.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.SLOW_TO_MARKET,
                    title: translate('sparkflow.useCase.productManagement.financialServices.slowToMarket.title'),
                    description: translate('sparkflow.useCase.productManagement.financialServices.slowToMarket.description'),
                    question: translate('sparkflow.useCase.productManagement.financialServices.slowToMarket.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.NPS_SLIPPAGE,
                    title: translate('sparkflow.useCase.productManagement.financialServices.npsSlippage.title'),
                    description: translate('sparkflow.useCase.productManagement.financialServices.npsSlippage.description'),
                    question: translate('sparkflow.useCase.productManagement.financialServices.npsSlippage.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.ADOPTION_ROOT_CAUSE,
                    title: translate('sparkflow.useCase.productManagement.financialServices.adoptionRootCause.title'),
                    description: translate('sparkflow.useCase.productManagement.financialServices.adoptionRootCause.description'),
                    question: translate('sparkflow.useCase.productManagement.financialServices.adoptionRootCause.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DELIVERY_BOTTLENECK,
                    title: translate('sparkflow.useCase.productManagement.financialServices.deliveryBottleneck.title'),
                    description: translate('sparkflow.useCase.productManagement.financialServices.deliveryBottleneck.description'),
                    question: translate('sparkflow.useCase.productManagement.financialServices.deliveryBottleneck.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.NPS_ADOPTION_RISK,
                    title: translate('sparkflow.useCase.productManagement.financialServices.npsAdoptionRisk.title'),
                    description: translate('sparkflow.useCase.productManagement.financialServices.npsAdoptionRisk.description'),
                    question: translate('sparkflow.useCase.productManagement.financialServices.npsAdoptionRisk.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.FINANCE_ACCOUNTING}__${IndustryId.FINANCIAL_SERVICES}`]: {
            normal: [
                {
                    id: UseCaseId.MARGIN_SQUEEZE,
                    title: translate('sparkflow.useCase.financeAccounting.financialServices.marginSqueeze.title'),
                    description: translate('sparkflow.useCase.financeAccounting.financialServices.marginSqueeze.description'),
                    question: translate('sparkflow.useCase.financeAccounting.financialServices.marginSqueeze.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DSO_RISK,
                    title: translate('sparkflow.useCase.financeAccounting.financialServices.dsoRisk.title'),
                    description: translate('sparkflow.useCase.financeAccounting.financialServices.dsoRisk.description'),
                    question: translate('sparkflow.useCase.financeAccounting.financialServices.dsoRisk.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.BUDGET_BLOWOUT,
                    title: translate('sparkflow.useCase.financeAccounting.financialServices.budgetBlowout.title'),
                    description: translate('sparkflow.useCase.financeAccounting.financialServices.budgetBlowout.description'),
                    question: translate('sparkflow.useCase.financeAccounting.financialServices.budgetBlowout.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.MARGIN_COMPRESSION,
                    title: translate('sparkflow.useCase.financeAccounting.financialServices.marginCompression.title'),
                    description: translate('sparkflow.useCase.financeAccounting.financialServices.marginCompression.description'),
                    question: translate('sparkflow.useCase.financeAccounting.financialServices.marginCompression.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.CASH_FLOW_RISK,
                    title: translate('sparkflow.useCase.financeAccounting.financialServices.cashFlowRisk.title'),
                    description: translate('sparkflow.useCase.financeAccounting.financialServices.cashFlowRisk.description'),
                    question: translate('sparkflow.useCase.financeAccounting.financialServices.cashFlowRisk.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.EBITDA_LEAK,
                    title: translate('sparkflow.useCase.financeAccounting.financialServices.ebitdaLeak.title'),
                    description: translate('sparkflow.useCase.financeAccounting.financialServices.ebitdaLeak.description'),
                    question: translate('sparkflow.useCase.financeAccounting.financialServices.ebitdaLeak.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.EXECUTIVE_LEADERSHIP}__${IndustryId.FINANCIAL_SERVICES}`]: {
            normal: [
                {
                    id: UseCaseId.REVENUE_VS_MARGIN,
                    title: translate('sparkflow.useCase.executiveLeadership.financialServices.revenueVsMargin.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.financialServices.revenueVsMargin.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.financialServices.revenueVsMargin.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.SATISFACTION_DIP,
                    title: translate('sparkflow.useCase.executiveLeadership.financialServices.satisfactionDip.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.financialServices.satisfactionDip.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.financialServices.satisfactionDip.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.MARKET_SHARE,
                    title: translate('sparkflow.useCase.executiveLeadership.financialServices.marketShare.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.financialServices.marketShare.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.financialServices.marketShare.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.PROFITABILITY_PARADOX,
                    title: translate('sparkflow.useCase.executiveLeadership.financialServices.profitabilityParadox.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.financialServices.profitabilityParadox.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.financialServices.profitabilityParadox.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.CLIENT_RETENTION_RISK,
                    title: translate('sparkflow.useCase.executiveLeadership.financialServices.clientRetentionRisk.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.financialServices.clientRetentionRisk.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.financialServices.clientRetentionRisk.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.OPEX_EFFICIENCY,
                    title: translate('sparkflow.useCase.executiveLeadership.financialServices.opexEfficiency.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.financialServices.opexEfficiency.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.financialServices.opexEfficiency.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.SOFTWARE_ENGINEERING}__${IndustryId.FINANCIAL_SERVICES}`]: {
            normal: [
                {
                    id: UseCaseId.BUILD_FAILURES,
                    title: translate('sparkflow.useCase.softwareEngineering.financialServices.buildFailures.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.financialServices.buildFailures.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.financialServices.buildFailures.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.LEAD_TIME_RISING,
                    title: translate('sparkflow.useCase.softwareEngineering.financialServices.leadTimeRising.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.financialServices.leadTimeRising.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.financialServices.leadTimeRising.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.INCIDENT_SURGE,
                    title: translate('sparkflow.useCase.softwareEngineering.financialServices.incidentSurge.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.financialServices.incidentSurge.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.financialServices.incidentSurge.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.BUILD_RELIABILITY,
                    title: translate('sparkflow.useCase.softwareEngineering.financialServices.buildReliability.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.financialServices.buildReliability.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.financialServices.buildReliability.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DELIVERY_SLOWDOWN,
                    title: translate('sparkflow.useCase.softwareEngineering.financialServices.deliverySlowdown.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.financialServices.deliverySlowdown.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.financialServices.deliverySlowdown.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.STABILITY_RISK,
                    title: translate('sparkflow.useCase.softwareEngineering.financialServices.stabilityRisk.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.financialServices.stabilityRisk.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.financialServices.stabilityRisk.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.DATA_ANALYTICS}__${IndustryId.FINANCIAL_SERVICES}`]: {
            normal: [
                {
                    id: UseCaseId.PIPELINE_FAILURES,
                    title: translate('sparkflow.useCase.dataAnalytics.financialServices.pipelineFailures.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.financialServices.pipelineFailures.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.financialServices.pipelineFailures.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.QUALITY_DROP,
                    title: translate('sparkflow.useCase.dataAnalytics.financialServices.qualityDrop.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.financialServices.qualityDrop.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.financialServices.qualityDrop.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.SLA_BREACHES,
                    title: translate('sparkflow.useCase.dataAnalytics.financialServices.slaBreaches.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.financialServices.slaBreaches.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.financialServices.slaBreaches.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.FAILURE_ROOT_CAUSE,
                    title: translate('sparkflow.useCase.dataAnalytics.financialServices.failureRootCause.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.financialServices.failureRootCause.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.financialServices.failureRootCause.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.LATENCY_AND_QUALITY,
                    title: translate('sparkflow.useCase.dataAnalytics.financialServices.latencyAndQuality.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.financialServices.latencyAndQuality.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.financialServices.latencyAndQuality.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.CAPACITY_RISK,
                    title: translate('sparkflow.useCase.dataAnalytics.financialServices.capacityRisk.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.financialServices.capacityRisk.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.financialServices.capacityRisk.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.SALES_ACCOUNT_MANAGEMENT}__${IndustryId.HEALTHCARE_LIFE_SCIENCES}`]: {
            normal: [
                {
                    id: UseCaseId.CONTRACT_WIN_RATE,
                    title: translate('sparkflow.useCase.salesAccountManagement.healthcareLifeSciences.contractWinRate.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.healthcareLifeSciences.contractWinRate.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.healthcareLifeSciences.contractWinRate.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.LONG_SALES_CYCLES,
                    title: translate('sparkflow.useCase.salesAccountManagement.healthcareLifeSciences.longSalesCycles.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.healthcareLifeSciences.longSalesCycles.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.healthcareLifeSciences.longSalesCycles.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.RENEWAL_RISK,
                    title: translate('sparkflow.useCase.salesAccountManagement.healthcareLifeSciences.renewalRisk.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.healthcareLifeSciences.renewalRisk.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.healthcareLifeSciences.renewalRisk.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.WIN_RATE_EROSION,
                    title: translate('sparkflow.useCase.salesAccountManagement.healthcareLifeSciences.winRateErosion.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.healthcareLifeSciences.winRateErosion.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.healthcareLifeSciences.winRateErosion.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.PIPELINE_STALL,
                    title: translate('sparkflow.useCase.salesAccountManagement.healthcareLifeSciences.pipelineStall.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.healthcareLifeSciences.pipelineStall.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.healthcareLifeSciences.pipelineStall.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.QUOTA_AND_RETENTION,
                    title: translate('sparkflow.useCase.salesAccountManagement.healthcareLifeSciences.quotaAndRetention.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.healthcareLifeSciences.quotaAndRetention.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.healthcareLifeSciences.quotaAndRetention.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.MARKETING_GROWTH}__${IndustryId.HEALTHCARE_LIFE_SCIENCES}`]: {
            normal: [
                {
                    id: UseCaseId.PATIENT_CAC_RISING,
                    title: translate('sparkflow.useCase.marketingGrowth.healthcareLifeSciences.patientCacRising.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.healthcareLifeSciences.patientCacRising.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.healthcareLifeSciences.patientCacRising.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.CONVERSION_FALL,
                    title: translate('sparkflow.useCase.marketingGrowth.healthcareLifeSciences.conversionFall.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.healthcareLifeSciences.conversionFall.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.healthcareLifeSciences.conversionFall.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.ROAS_EROSION,
                    title: translate('sparkflow.useCase.marketingGrowth.healthcareLifeSciences.roasErosion.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.healthcareLifeSciences.roasErosion.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.healthcareLifeSciences.roasErosion.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.CAC_BY_CHANNEL,
                    title: translate('sparkflow.useCase.marketingGrowth.healthcareLifeSciences.cacByChannel.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.healthcareLifeSciences.cacByChannel.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.healthcareLifeSciences.cacByChannel.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.MQL_TO_PATIENT_GAP,
                    title: translate('sparkflow.useCase.marketingGrowth.healthcareLifeSciences.mqlToPatientGap.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.healthcareLifeSciences.mqlToPatientGap.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.healthcareLifeSciences.mqlToPatientGap.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.REGIONAL_REACH,
                    title: translate('sparkflow.useCase.marketingGrowth.healthcareLifeSciences.regionalReach.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.healthcareLifeSciences.regionalReach.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.healthcareLifeSciences.regionalReach.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.PRODUCT_MANAGEMENT}__${IndustryId.HEALTHCARE_LIFE_SCIENCES}`]: {
            normal: [
                {
                    id: UseCaseId.ADOPTION_BY_SERVICE,
                    title: translate('sparkflow.useCase.productManagement.healthcareLifeSciences.adoptionByService.title'),
                    description: translate('sparkflow.useCase.productManagement.healthcareLifeSciences.adoptionByService.description'),
                    question: translate('sparkflow.useCase.productManagement.healthcareLifeSciences.adoptionByService.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.SLOW_RELEASES,
                    title: translate('sparkflow.useCase.productManagement.healthcareLifeSciences.slowReleases.title'),
                    description: translate('sparkflow.useCase.productManagement.healthcareLifeSciences.slowReleases.description'),
                    question: translate('sparkflow.useCase.productManagement.healthcareLifeSciences.slowReleases.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.PATIENT_NPS_GAP,
                    title: translate('sparkflow.useCase.productManagement.healthcareLifeSciences.patientNpsGap.title'),
                    description: translate('sparkflow.useCase.productManagement.healthcareLifeSciences.patientNpsGap.description'),
                    question: translate('sparkflow.useCase.productManagement.healthcareLifeSciences.patientNpsGap.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.ADOPTION_AND_CARE_GAP,
                    title: translate('sparkflow.useCase.productManagement.healthcareLifeSciences.adoptionAndCareGap.title'),
                    description: translate('sparkflow.useCase.productManagement.healthcareLifeSciences.adoptionAndCareGap.description'),
                    question: translate('sparkflow.useCase.productManagement.healthcareLifeSciences.adoptionAndCareGap.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.RELEASE_LAG_RISK,
                    title: translate('sparkflow.useCase.productManagement.healthcareLifeSciences.releaseLagRisk.title'),
                    description: translate('sparkflow.useCase.productManagement.healthcareLifeSciences.releaseLagRisk.description'),
                    question: translate('sparkflow.useCase.productManagement.healthcareLifeSciences.releaseLagRisk.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.NPS_AND_ADOPTION,
                    title: translate('sparkflow.useCase.productManagement.healthcareLifeSciences.npsAndAdoption.title'),
                    description: translate('sparkflow.useCase.productManagement.healthcareLifeSciences.npsAndAdoption.description'),
                    question: translate('sparkflow.useCase.productManagement.healthcareLifeSciences.npsAndAdoption.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.FINANCE_ACCOUNTING}__${IndustryId.HEALTHCARE_LIFE_SCIENCES}`]: {
            normal: [
                {
                    id: UseCaseId.MARGIN_COMPRESSION,
                    title: translate('sparkflow.useCase.financeAccounting.healthcareLifeSciences.marginCompression.title'),
                    description: translate('sparkflow.useCase.financeAccounting.healthcareLifeSciences.marginCompression.description'),
                    question: translate('sparkflow.useCase.financeAccounting.healthcareLifeSciences.marginCompression.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DSO_RISK,
                    title: translate('sparkflow.useCase.financeAccounting.healthcareLifeSciences.dsoRisk.title'),
                    description: translate('sparkflow.useCase.financeAccounting.healthcareLifeSciences.dsoRisk.description'),
                    question: translate('sparkflow.useCase.financeAccounting.healthcareLifeSciences.dsoRisk.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.BUDGET_OVERRUNS,
                    title: translate('sparkflow.useCase.financeAccounting.healthcareLifeSciences.budgetOverruns.title'),
                    description: translate('sparkflow.useCase.financeAccounting.healthcareLifeSciences.budgetOverruns.description'),
                    question: translate('sparkflow.useCase.financeAccounting.healthcareLifeSciences.budgetOverruns.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.REVENUE_COST_SQUEEZE,
                    title: translate('sparkflow.useCase.financeAccounting.healthcareLifeSciences.revenueCostSqueeze.title'),
                    description: translate('sparkflow.useCase.financeAccounting.healthcareLifeSciences.revenueCostSqueeze.description'),
                    question: translate('sparkflow.useCase.financeAccounting.healthcareLifeSciences.revenueCostSqueeze.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.COLLECTIONS_RISK,
                    title: translate('sparkflow.useCase.financeAccounting.healthcareLifeSciences.collectionsRisk.title'),
                    description: translate('sparkflow.useCase.financeAccounting.healthcareLifeSciences.collectionsRisk.description'),
                    question: translate('sparkflow.useCase.financeAccounting.healthcareLifeSciences.collectionsRisk.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.EBITDA_EROSION,
                    title: translate('sparkflow.useCase.financeAccounting.healthcareLifeSciences.ebitdaErosion.title'),
                    description: translate('sparkflow.useCase.financeAccounting.healthcareLifeSciences.ebitdaErosion.description'),
                    question: translate('sparkflow.useCase.financeAccounting.healthcareLifeSciences.ebitdaErosion.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.EXECUTIVE_LEADERSHIP}__${IndustryId.HEALTHCARE_LIFE_SCIENCES}`]: {
            normal: [
                {
                    id: UseCaseId.REVENUE_VS_MARGIN,
                    title: translate('sparkflow.useCase.executiveLeadership.healthcareLifeSciences.revenueVsMargin.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.healthcareLifeSciences.revenueVsMargin.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.healthcareLifeSciences.revenueVsMargin.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.PATIENT_SATISFACTION,
                    title: translate('sparkflow.useCase.executiveLeadership.healthcareLifeSciences.patientSatisfaction.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.healthcareLifeSciences.patientSatisfaction.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.healthcareLifeSciences.patientSatisfaction.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.MARKET_SHARE,
                    title: translate('sparkflow.useCase.executiveLeadership.healthcareLifeSciences.marketShare.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.healthcareLifeSciences.marketShare.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.healthcareLifeSciences.marketShare.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.MARGIN_PARADOX,
                    title: translate('sparkflow.useCase.executiveLeadership.healthcareLifeSciences.marginParadox.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.healthcareLifeSciences.marginParadox.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.healthcareLifeSciences.marginParadox.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.PATIENT_EXPERIENCE_RISK,
                    title: translate('sparkflow.useCase.executiveLeadership.healthcareLifeSciences.patientExperienceRisk.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.healthcareLifeSciences.patientExperienceRisk.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.healthcareLifeSciences.patientExperienceRisk.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.OPEX_AND_OUTCOMES,
                    title: translate('sparkflow.useCase.executiveLeadership.healthcareLifeSciences.opexAndOutcomes.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.healthcareLifeSciences.opexAndOutcomes.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.healthcareLifeSciences.opexAndOutcomes.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.SOFTWARE_ENGINEERING}__${IndustryId.HEALTHCARE_LIFE_SCIENCES}`]: {
            normal: [
                {
                    id: UseCaseId.BUILD_RELIABILITY,
                    title: translate('sparkflow.useCase.softwareEngineering.healthcareLifeSciences.buildReliability.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.healthcareLifeSciences.buildReliability.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.healthcareLifeSciences.buildReliability.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.SLOW_DEPLOYMENTS,
                    title: translate('sparkflow.useCase.softwareEngineering.healthcareLifeSciences.slowDeployments.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.healthcareLifeSciences.slowDeployments.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.healthcareLifeSciences.slowDeployments.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.INCIDENT_RATE,
                    title: translate('sparkflow.useCase.softwareEngineering.healthcareLifeSciences.incidentRate.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.healthcareLifeSciences.incidentRate.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.healthcareLifeSciences.incidentRate.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.BUILD_FAILURE_CAUSE,
                    title: translate('sparkflow.useCase.softwareEngineering.healthcareLifeSciences.buildFailureCause.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.healthcareLifeSciences.buildFailureCause.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.healthcareLifeSciences.buildFailureCause.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DELIVERY_LAG,
                    title: translate('sparkflow.useCase.softwareEngineering.healthcareLifeSciences.deliveryLag.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.healthcareLifeSciences.deliveryLag.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.healthcareLifeSciences.deliveryLag.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.CLINICAL_SYSTEM_RISK,
                    title: translate('sparkflow.useCase.softwareEngineering.healthcareLifeSciences.clinicalSystemRisk.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.healthcareLifeSciences.clinicalSystemRisk.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.healthcareLifeSciences.clinicalSystemRisk.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.DATA_ANALYTICS}__${IndustryId.HEALTHCARE_LIFE_SCIENCES}`]: {
            normal: [
                {
                    id: UseCaseId.PIPELINE_FAILURES,
                    title: translate('sparkflow.useCase.dataAnalytics.healthcareLifeSciences.pipelineFailures.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.healthcareLifeSciences.pipelineFailures.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.healthcareLifeSciences.pipelineFailures.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DATA_QUALITY_RISK,
                    title: translate('sparkflow.useCase.dataAnalytics.healthcareLifeSciences.dataQualityRisk.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.healthcareLifeSciences.dataQualityRisk.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.healthcareLifeSciences.dataQualityRisk.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.SLA_MISSES,
                    title: translate('sparkflow.useCase.dataAnalytics.healthcareLifeSciences.slaMisses.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.healthcareLifeSciences.slaMisses.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.healthcareLifeSciences.slaMisses.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.FAILURE_ANALYSIS,
                    title: translate('sparkflow.useCase.dataAnalytics.healthcareLifeSciences.failureAnalysis.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.healthcareLifeSciences.failureAnalysis.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.healthcareLifeSciences.failureAnalysis.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.LATENCY_QUALITY_GAP,
                    title: translate('sparkflow.useCase.dataAnalytics.healthcareLifeSciences.latencyQualityGap.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.healthcareLifeSciences.latencyQualityGap.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.healthcareLifeSciences.latencyQualityGap.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.VOLUME_VS_THROUGHPUT,
                    title: translate('sparkflow.useCase.dataAnalytics.healthcareLifeSciences.volumeVsThroughput.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.healthcareLifeSciences.volumeVsThroughput.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.healthcareLifeSciences.volumeVsThroughput.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.SALES_ACCOUNT_MANAGEMENT}__${IndustryId.HOSPITALITY_TRANSPORTATION}`]: {
            normal: [
                {
                    id: UseCaseId.BOOKING_WIN_RATE,
                    title: translate('sparkflow.useCase.salesAccountManagement.hospitalityTransportation.bookingWinRate.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.hospitalityTransportation.bookingWinRate.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.hospitalityTransportation.bookingWinRate.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.LONG_CLOSE_CYCLES,
                    title: translate('sparkflow.useCase.salesAccountManagement.hospitalityTransportation.longCloseCycles.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.hospitalityTransportation.longCloseCycles.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.hospitalityTransportation.longCloseCycles.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.RENEWAL_EROSION,
                    title: translate('sparkflow.useCase.salesAccountManagement.hospitalityTransportation.renewalErosion.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.hospitalityTransportation.renewalErosion.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.hospitalityTransportation.renewalErosion.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.CONVERSION_COLLAPSE,
                    title: translate('sparkflow.useCase.salesAccountManagement.hospitalityTransportation.conversionCollapse.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.hospitalityTransportation.conversionCollapse.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.hospitalityTransportation.conversionCollapse.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.PIPELINE_DEAD_ENDS,
                    title: translate('sparkflow.useCase.salesAccountManagement.hospitalityTransportation.pipelineDeadEnds.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.hospitalityTransportation.pipelineDeadEnds.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.hospitalityTransportation.pipelineDeadEnds.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.QUOTA_AND_CHURN,
                    title: translate('sparkflow.useCase.salesAccountManagement.hospitalityTransportation.quotaAndChurn.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.hospitalityTransportation.quotaAndChurn.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.hospitalityTransportation.quotaAndChurn.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.MARKETING_GROWTH}__${IndustryId.HOSPITALITY_TRANSPORTATION}`]: {
            normal: [
                {
                    id: UseCaseId.GUEST_CAC_RISING,
                    title: translate('sparkflow.useCase.marketingGrowth.hospitalityTransportation.guestCacRising.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.hospitalityTransportation.guestCacRising.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.hospitalityTransportation.guestCacRising.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.BOOKING_CONVERSION,
                    title: translate('sparkflow.useCase.marketingGrowth.hospitalityTransportation.bookingConversion.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.hospitalityTransportation.bookingConversion.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.hospitalityTransportation.bookingConversion.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.ROAS_BY_REGION,
                    title: translate('sparkflow.useCase.marketingGrowth.hospitalityTransportation.roasByRegion.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.hospitalityTransportation.roasByRegion.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.hospitalityTransportation.roasByRegion.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.CAC_VS_DIRECT,
                    title: translate('sparkflow.useCase.marketingGrowth.hospitalityTransportation.cacVsDirect.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.hospitalityTransportation.cacVsDirect.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.hospitalityTransportation.cacVsDirect.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.MQL_WASTE,
                    title: translate('sparkflow.useCase.marketingGrowth.hospitalityTransportation.mqlWaste.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.hospitalityTransportation.mqlWaste.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.hospitalityTransportation.mqlWaste.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.REGIONAL_REACH,
                    title: translate('sparkflow.useCase.marketingGrowth.hospitalityTransportation.regionalReach.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.hospitalityTransportation.regionalReach.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.hospitalityTransportation.regionalReach.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.PRODUCT_MANAGEMENT}__${IndustryId.HOSPITALITY_TRANSPORTATION}`]: {
            normal: [
                {
                    id: UseCaseId.GUEST_ADOPTION,
                    title: translate('sparkflow.useCase.productManagement.hospitalityTransportation.guestAdoption.title'),
                    description: translate('sparkflow.useCase.productManagement.hospitalityTransportation.guestAdoption.description'),
                    question: translate('sparkflow.useCase.productManagement.hospitalityTransportation.guestAdoption.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.SLOW_FEATURES,
                    title: translate('sparkflow.useCase.productManagement.hospitalityTransportation.slowFeatures.title'),
                    description: translate('sparkflow.useCase.productManagement.hospitalityTransportation.slowFeatures.description'),
                    question: translate('sparkflow.useCase.productManagement.hospitalityTransportation.slowFeatures.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.GUEST_NPS_GAP,
                    title: translate('sparkflow.useCase.productManagement.hospitalityTransportation.guestNpsGap.title'),
                    description: translate('sparkflow.useCase.productManagement.hospitalityTransportation.guestNpsGap.description'),
                    question: translate('sparkflow.useCase.productManagement.hospitalityTransportation.guestNpsGap.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.ADOPTION_COLLAPSE,
                    title: translate('sparkflow.useCase.productManagement.hospitalityTransportation.adoptionCollapse.title'),
                    description: translate('sparkflow.useCase.productManagement.hospitalityTransportation.adoptionCollapse.description'),
                    question: translate('sparkflow.useCase.productManagement.hospitalityTransportation.adoptionCollapse.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.RELEASE_BOTTLENECK,
                    title: translate('sparkflow.useCase.productManagement.hospitalityTransportation.releaseBottleneck.title'),
                    description: translate('sparkflow.useCase.productManagement.hospitalityTransportation.releaseBottleneck.description'),
                    question: translate('sparkflow.useCase.productManagement.hospitalityTransportation.releaseBottleneck.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.NPS_ADOPTION_RISK,
                    title: translate('sparkflow.useCase.productManagement.hospitalityTransportation.npsAdoptionRisk.title'),
                    description: translate('sparkflow.useCase.productManagement.hospitalityTransportation.npsAdoptionRisk.description'),
                    question: translate('sparkflow.useCase.productManagement.hospitalityTransportation.npsAdoptionRisk.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.FINANCE_ACCOUNTING}__${IndustryId.HOSPITALITY_TRANSPORTATION}`]: {
            normal: [
                {
                    id: UseCaseId.REVPAR_VS_COSTS,
                    title: translate('sparkflow.useCase.financeAccounting.hospitalityTransportation.revparVsCosts.title'),
                    description: translate('sparkflow.useCase.financeAccounting.hospitalityTransportation.revparVsCosts.description'),
                    question: translate('sparkflow.useCase.financeAccounting.hospitalityTransportation.revparVsCosts.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DSO_RISK,
                    title: translate('sparkflow.useCase.financeAccounting.hospitalityTransportation.dsoRisk.title'),
                    description: translate('sparkflow.useCase.financeAccounting.hospitalityTransportation.dsoRisk.description'),
                    question: translate('sparkflow.useCase.financeAccounting.hospitalityTransportation.dsoRisk.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.BUDGET_OVERRUNS,
                    title: translate('sparkflow.useCase.financeAccounting.hospitalityTransportation.budgetOverruns.title'),
                    description: translate('sparkflow.useCase.financeAccounting.hospitalityTransportation.budgetOverruns.description'),
                    question: translate('sparkflow.useCase.financeAccounting.hospitalityTransportation.budgetOverruns.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.MARGIN_SQUEEZE,
                    title: translate('sparkflow.useCase.financeAccounting.hospitalityTransportation.marginSqueeze.title'),
                    description: translate('sparkflow.useCase.financeAccounting.hospitalityTransportation.marginSqueeze.description'),
                    question: translate('sparkflow.useCase.financeAccounting.hospitalityTransportation.marginSqueeze.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.AR_COLLECTIONS,
                    title: translate('sparkflow.useCase.financeAccounting.hospitalityTransportation.arCollections.title'),
                    description: translate('sparkflow.useCase.financeAccounting.hospitalityTransportation.arCollections.description'),
                    question: translate('sparkflow.useCase.financeAccounting.hospitalityTransportation.arCollections.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.EBITDA_EROSION,
                    title: translate('sparkflow.useCase.financeAccounting.hospitalityTransportation.ebitdaErosion.title'),
                    description: translate('sparkflow.useCase.financeAccounting.hospitalityTransportation.ebitdaErosion.description'),
                    question: translate('sparkflow.useCase.financeAccounting.hospitalityTransportation.ebitdaErosion.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.EXECUTIVE_LEADERSHIP}__${IndustryId.HOSPITALITY_TRANSPORTATION}`]: {
            normal: [
                {
                    id: UseCaseId.REVENUE_VS_MARGIN,
                    title: translate('sparkflow.useCase.executiveLeadership.hospitalityTransportation.revenueVsMargin.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.hospitalityTransportation.revenueVsMargin.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.hospitalityTransportation.revenueVsMargin.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.GUEST_SATISFACTION,
                    title: translate('sparkflow.useCase.executiveLeadership.hospitalityTransportation.guestSatisfaction.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.hospitalityTransportation.guestSatisfaction.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.hospitalityTransportation.guestSatisfaction.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.MARKET_SHARE,
                    title: translate('sparkflow.useCase.executiveLeadership.hospitalityTransportation.marketShare.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.hospitalityTransportation.marketShare.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.hospitalityTransportation.marketShare.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.PROFITABILITY_GAP,
                    title: translate('sparkflow.useCase.executiveLeadership.hospitalityTransportation.profitabilityGap.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.hospitalityTransportation.profitabilityGap.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.hospitalityTransportation.profitabilityGap.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.GUEST_EXPERIENCE_RISK,
                    title: translate('sparkflow.useCase.executiveLeadership.hospitalityTransportation.guestExperienceRisk.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.hospitalityTransportation.guestExperienceRisk.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.hospitalityTransportation.guestExperienceRisk.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.OPEX_RETURNS,
                    title: translate('sparkflow.useCase.executiveLeadership.hospitalityTransportation.opexReturns.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.hospitalityTransportation.opexReturns.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.hospitalityTransportation.opexReturns.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.SOFTWARE_ENGINEERING}__${IndustryId.HOSPITALITY_TRANSPORTATION}`]: {
            normal: [
                {
                    id: UseCaseId.BUILD_FAILURES,
                    title: translate('sparkflow.useCase.softwareEngineering.hospitalityTransportation.buildFailures.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.hospitalityTransportation.buildFailures.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.hospitalityTransportation.buildFailures.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DEPLOYMENT_LAG,
                    title: translate('sparkflow.useCase.softwareEngineering.hospitalityTransportation.deploymentLag.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.hospitalityTransportation.deploymentLag.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.hospitalityTransportation.deploymentLag.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.INCIDENT_SURGE,
                    title: translate('sparkflow.useCase.softwareEngineering.hospitalityTransportation.incidentSurge.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.hospitalityTransportation.incidentSurge.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.hospitalityTransportation.incidentSurge.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.BUILD_RELIABILITY,
                    title: translate('sparkflow.useCase.softwareEngineering.hospitalityTransportation.buildReliability.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.hospitalityTransportation.buildReliability.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.hospitalityTransportation.buildReliability.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DELIVERY_BOTTLENECK,
                    title: translate('sparkflow.useCase.softwareEngineering.hospitalityTransportation.deliveryBottleneck.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.hospitalityTransportation.deliveryBottleneck.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.hospitalityTransportation.deliveryBottleneck.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.PLATFORM_STABILITY,
                    title: translate('sparkflow.useCase.softwareEngineering.hospitalityTransportation.platformStability.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.hospitalityTransportation.platformStability.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.hospitalityTransportation.platformStability.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.DATA_ANALYTICS}__${IndustryId.HOSPITALITY_TRANSPORTATION}`]: {
            normal: [
                {
                    id: UseCaseId.PIPELINE_FAILURES,
                    title: translate('sparkflow.useCase.dataAnalytics.hospitalityTransportation.pipelineFailures.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.hospitalityTransportation.pipelineFailures.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.hospitalityTransportation.pipelineFailures.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DATA_QUALITY,
                    title: translate('sparkflow.useCase.dataAnalytics.hospitalityTransportation.dataQuality.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.hospitalityTransportation.dataQuality.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.hospitalityTransportation.dataQuality.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.SLA_MISSES,
                    title: translate('sparkflow.useCase.dataAnalytics.hospitalityTransportation.slaMisses.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.hospitalityTransportation.slaMisses.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.hospitalityTransportation.slaMisses.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.FAILURE_ROOT_CAUSE,
                    title: translate('sparkflow.useCase.dataAnalytics.hospitalityTransportation.failureRootCause.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.hospitalityTransportation.failureRootCause.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.hospitalityTransportation.failureRootCause.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.LATENCY_QUALITY_GAP,
                    title: translate('sparkflow.useCase.dataAnalytics.hospitalityTransportation.latencyQualityGap.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.hospitalityTransportation.latencyQualityGap.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.hospitalityTransportation.latencyQualityGap.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.CAPACITY_RISK,
                    title: translate('sparkflow.useCase.dataAnalytics.hospitalityTransportation.capacityRisk.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.hospitalityTransportation.capacityRisk.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.hospitalityTransportation.capacityRisk.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.SALES_ACCOUNT_MANAGEMENT}__${IndustryId.MANUFACTURING_LOGISTICS}`]: {
            normal: [
                {
                    id: UseCaseId.WIN_RATE_DROP,
                    title: translate('sparkflow.useCase.salesAccountManagement.manufacturingLogistics.winRateDrop.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.manufacturingLogistics.winRateDrop.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.manufacturingLogistics.winRateDrop.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.RFQ_CYCLE_LENGTH,
                    title: translate('sparkflow.useCase.salesAccountManagement.manufacturingLogistics.rfqCycleLength.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.manufacturingLogistics.rfqCycleLength.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.manufacturingLogistics.rfqCycleLength.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.RENEWAL_RISK,
                    title: translate('sparkflow.useCase.salesAccountManagement.manufacturingLogistics.renewalRisk.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.manufacturingLogistics.renewalRisk.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.manufacturingLogistics.renewalRisk.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.WIN_RATE_EROSION,
                    title: translate('sparkflow.useCase.salesAccountManagement.manufacturingLogistics.winRateErosion.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.manufacturingLogistics.winRateErosion.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.manufacturingLogistics.winRateErosion.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.PIPELINE_STALL,
                    title: translate('sparkflow.useCase.salesAccountManagement.manufacturingLogistics.pipelineStall.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.manufacturingLogistics.pipelineStall.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.manufacturingLogistics.pipelineStall.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.QUOTA_AND_CHURN,
                    title: translate('sparkflow.useCase.salesAccountManagement.manufacturingLogistics.quotaAndChurn.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.manufacturingLogistics.quotaAndChurn.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.manufacturingLogistics.quotaAndChurn.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.MARKETING_GROWTH}__${IndustryId.MANUFACTURING_LOGISTICS}`]: {
            normal: [
                {
                    id: UseCaseId.LEAD_CAC_RISING,
                    title: translate('sparkflow.useCase.marketingGrowth.manufacturingLogistics.leadCacRising.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.manufacturingLogistics.leadCacRising.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.manufacturingLogistics.leadCacRising.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.MQL_CONVERSION,
                    title: translate('sparkflow.useCase.marketingGrowth.manufacturingLogistics.mqlConversion.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.manufacturingLogistics.mqlConversion.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.manufacturingLogistics.mqlConversion.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.TRADE_SHOW_ROAS,
                    title: translate('sparkflow.useCase.marketingGrowth.manufacturingLogistics.tradeShowRoas.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.manufacturingLogistics.tradeShowRoas.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.manufacturingLogistics.tradeShowRoas.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.CAC_BREAKDOWN,
                    title: translate('sparkflow.useCase.marketingGrowth.manufacturingLogistics.cacBreakdown.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.manufacturingLogistics.cacBreakdown.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.manufacturingLogistics.cacBreakdown.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.MQL_QUALITY_GAP,
                    title: translate('sparkflow.useCase.marketingGrowth.manufacturingLogistics.mqlQualityGap.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.manufacturingLogistics.mqlQualityGap.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.manufacturingLogistics.mqlQualityGap.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.REGIONAL_REACH,
                    title: translate('sparkflow.useCase.marketingGrowth.manufacturingLogistics.regionalReach.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.manufacturingLogistics.regionalReach.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.manufacturingLogistics.regionalReach.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.PRODUCT_MANAGEMENT}__${IndustryId.MANUFACTURING_LOGISTICS}`]: {
            normal: [
                {
                    id: UseCaseId.ADOPTION_DECLINE,
                    title: translate('sparkflow.useCase.productManagement.manufacturingLogistics.adoptionDecline.title'),
                    description: translate('sparkflow.useCase.productManagement.manufacturingLogistics.adoptionDecline.description'),
                    question: translate('sparkflow.useCase.productManagement.manufacturingLogistics.adoptionDecline.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.SLOW_TO_MARKET,
                    title: translate('sparkflow.useCase.productManagement.manufacturingLogistics.slowToMarket.title'),
                    description: translate('sparkflow.useCase.productManagement.manufacturingLogistics.slowToMarket.description'),
                    question: translate('sparkflow.useCase.productManagement.manufacturingLogistics.slowToMarket.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.NPS_SLIPPAGE,
                    title: translate('sparkflow.useCase.productManagement.manufacturingLogistics.npsSlippage.title'),
                    description: translate('sparkflow.useCase.productManagement.manufacturingLogistics.npsSlippage.description'),
                    question: translate('sparkflow.useCase.productManagement.manufacturingLogistics.npsSlippage.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.ADOPTION_GAP,
                    title: translate('sparkflow.useCase.productManagement.manufacturingLogistics.adoptionGap.title'),
                    description: translate('sparkflow.useCase.productManagement.manufacturingLogistics.adoptionGap.description'),
                    question: translate('sparkflow.useCase.productManagement.manufacturingLogistics.adoptionGap.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.RELEASE_LAG,
                    title: translate('sparkflow.useCase.productManagement.manufacturingLogistics.releaseLag.title'),
                    description: translate('sparkflow.useCase.productManagement.manufacturingLogistics.releaseLag.description'),
                    question: translate('sparkflow.useCase.productManagement.manufacturingLogistics.releaseLag.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.NPS_ADOPTION_RISK,
                    title: translate('sparkflow.useCase.productManagement.manufacturingLogistics.npsAdoptionRisk.title'),
                    description: translate('sparkflow.useCase.productManagement.manufacturingLogistics.npsAdoptionRisk.description'),
                    question: translate('sparkflow.useCase.productManagement.manufacturingLogistics.npsAdoptionRisk.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.FINANCE_ACCOUNTING}__${IndustryId.MANUFACTURING_LOGISTICS}`]: {
            normal: [
                {
                    id: UseCaseId.MARGIN_SQUEEZE,
                    title: translate('sparkflow.useCase.financeAccounting.manufacturingLogistics.marginSqueeze.title'),
                    description: translate('sparkflow.useCase.financeAccounting.manufacturingLogistics.marginSqueeze.description'),
                    question: translate('sparkflow.useCase.financeAccounting.manufacturingLogistics.marginSqueeze.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DSO_RISK,
                    title: translate('sparkflow.useCase.financeAccounting.manufacturingLogistics.dsoRisk.title'),
                    description: translate('sparkflow.useCase.financeAccounting.manufacturingLogistics.dsoRisk.description'),
                    question: translate('sparkflow.useCase.financeAccounting.manufacturingLogistics.dsoRisk.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.BUDGET_OVERRUNS,
                    title: translate('sparkflow.useCase.financeAccounting.manufacturingLogistics.budgetOverruns.title'),
                    description: translate('sparkflow.useCase.financeAccounting.manufacturingLogistics.budgetOverruns.description'),
                    question: translate('sparkflow.useCase.financeAccounting.manufacturingLogistics.budgetOverruns.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.REVENUE_COST_GAP,
                    title: translate('sparkflow.useCase.financeAccounting.manufacturingLogistics.revenueCostGap.title'),
                    description: translate('sparkflow.useCase.financeAccounting.manufacturingLogistics.revenueCostGap.description'),
                    question: translate('sparkflow.useCase.financeAccounting.manufacturingLogistics.revenueCostGap.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.AR_COLLECTIONS_RISK,
                    title: translate('sparkflow.useCase.financeAccounting.manufacturingLogistics.arCollectionsRisk.title'),
                    description: translate('sparkflow.useCase.financeAccounting.manufacturingLogistics.arCollectionsRisk.description'),
                    question: translate('sparkflow.useCase.financeAccounting.manufacturingLogistics.arCollectionsRisk.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.EBITDA_EROSION,
                    title: translate('sparkflow.useCase.financeAccounting.manufacturingLogistics.ebitdaErosion.title'),
                    description: translate('sparkflow.useCase.financeAccounting.manufacturingLogistics.ebitdaErosion.description'),
                    question: translate('sparkflow.useCase.financeAccounting.manufacturingLogistics.ebitdaErosion.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.EXECUTIVE_LEADERSHIP}__${IndustryId.MANUFACTURING_LOGISTICS}`]: {
            normal: [
                {
                    id: UseCaseId.REVENUE_VS_MARGIN,
                    title: translate('sparkflow.useCase.executiveLeadership.manufacturingLogistics.revenueVsMargin.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.manufacturingLogistics.revenueVsMargin.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.manufacturingLogistics.revenueVsMargin.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.CUSTOMER_SATISFACTION,
                    title: translate('sparkflow.useCase.executiveLeadership.manufacturingLogistics.customerSatisfaction.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.manufacturingLogistics.customerSatisfaction.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.manufacturingLogistics.customerSatisfaction.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.MARKET_SHARE,
                    title: translate('sparkflow.useCase.executiveLeadership.manufacturingLogistics.marketShare.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.manufacturingLogistics.marketShare.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.manufacturingLogistics.marketShare.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.PROFITABILITY_PARADOX,
                    title: translate('sparkflow.useCase.executiveLeadership.manufacturingLogistics.profitabilityParadox.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.manufacturingLogistics.profitabilityParadox.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.manufacturingLogistics.profitabilityParadox.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.CUSTOMER_RETENTION_RISK,
                    title: translate('sparkflow.useCase.executiveLeadership.manufacturingLogistics.customerRetentionRisk.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.manufacturingLogistics.customerRetentionRisk.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.manufacturingLogistics.customerRetentionRisk.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.OPEX_EFFICIENCY,
                    title: translate('sparkflow.useCase.executiveLeadership.manufacturingLogistics.opexEfficiency.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.manufacturingLogistics.opexEfficiency.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.manufacturingLogistics.opexEfficiency.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.SOFTWARE_ENGINEERING}__${IndustryId.MANUFACTURING_LOGISTICS}`]: {
            normal: [
                {
                    id: UseCaseId.BUILD_FAILURES,
                    title: translate('sparkflow.useCase.softwareEngineering.manufacturingLogistics.buildFailures.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.manufacturingLogistics.buildFailures.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.manufacturingLogistics.buildFailures.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DEPLOYMENT_LAG,
                    title: translate('sparkflow.useCase.softwareEngineering.manufacturingLogistics.deploymentLag.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.manufacturingLogistics.deploymentLag.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.manufacturingLogistics.deploymentLag.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.INCIDENT_RATE,
                    title: translate('sparkflow.useCase.softwareEngineering.manufacturingLogistics.incidentRate.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.manufacturingLogistics.incidentRate.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.manufacturingLogistics.incidentRate.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.BUILD_RELIABILITY,
                    title: translate('sparkflow.useCase.softwareEngineering.manufacturingLogistics.buildReliability.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.manufacturingLogistics.buildReliability.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.manufacturingLogistics.buildReliability.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DELIVERY_SLOWDOWN,
                    title: translate('sparkflow.useCase.softwareEngineering.manufacturingLogistics.deliverySlowdown.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.manufacturingLogistics.deliverySlowdown.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.manufacturingLogistics.deliverySlowdown.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.PLATFORM_STABILITY,
                    title: translate('sparkflow.useCase.softwareEngineering.manufacturingLogistics.platformStability.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.manufacturingLogistics.platformStability.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.manufacturingLogistics.platformStability.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.DATA_ANALYTICS}__${IndustryId.MANUFACTURING_LOGISTICS}`]: {
            normal: [
                {
                    id: UseCaseId.PIPELINE_FAILURES,
                    title: translate('sparkflow.useCase.dataAnalytics.manufacturingLogistics.pipelineFailures.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.manufacturingLogistics.pipelineFailures.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.manufacturingLogistics.pipelineFailures.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DATA_QUALITY,
                    title: translate('sparkflow.useCase.dataAnalytics.manufacturingLogistics.dataQuality.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.manufacturingLogistics.dataQuality.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.manufacturingLogistics.dataQuality.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.SLA_MISSES,
                    title: translate('sparkflow.useCase.dataAnalytics.manufacturingLogistics.slaMisses.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.manufacturingLogistics.slaMisses.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.manufacturingLogistics.slaMisses.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.FAILURE_ANALYSIS,
                    title: translate('sparkflow.useCase.dataAnalytics.manufacturingLogistics.failureAnalysis.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.manufacturingLogistics.failureAnalysis.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.manufacturingLogistics.failureAnalysis.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.LATENCY_QUALITY_GAP,
                    title: translate('sparkflow.useCase.dataAnalytics.manufacturingLogistics.latencyQualityGap.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.manufacturingLogistics.latencyQualityGap.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.manufacturingLogistics.latencyQualityGap.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.CAPACITY_RISK,
                    title: translate('sparkflow.useCase.dataAnalytics.manufacturingLogistics.capacityRisk.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.manufacturingLogistics.capacityRisk.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.manufacturingLogistics.capacityRisk.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.SALES_ACCOUNT_MANAGEMENT}__${IndustryId.MEDIA_TELECOM}`]: {
            normal: [
                {
                    id: UseCaseId.WIN_RATE_DROP,
                    title: translate('sparkflow.useCase.salesAccountManagement.mediaTelecom.winRateDrop.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.mediaTelecom.winRateDrop.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.mediaTelecom.winRateDrop.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.LONG_CLOSE_CYCLES,
                    title: translate('sparkflow.useCase.salesAccountManagement.mediaTelecom.longCloseCycles.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.mediaTelecom.longCloseCycles.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.mediaTelecom.longCloseCycles.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.RENEWAL_EROSION,
                    title: translate('sparkflow.useCase.salesAccountManagement.mediaTelecom.renewalErosion.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.mediaTelecom.renewalErosion.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.mediaTelecom.renewalErosion.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.WIN_RATE_COLLAPSE,
                    title: translate('sparkflow.useCase.salesAccountManagement.mediaTelecom.winRateCollapse.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.mediaTelecom.winRateCollapse.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.mediaTelecom.winRateCollapse.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.PIPELINE_STALL,
                    title: translate('sparkflow.useCase.salesAccountManagement.mediaTelecom.pipelineStall.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.mediaTelecom.pipelineStall.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.mediaTelecom.pipelineStall.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.QUOTA_AND_CHURN,
                    title: translate('sparkflow.useCase.salesAccountManagement.mediaTelecom.quotaAndChurn.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.mediaTelecom.quotaAndChurn.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.mediaTelecom.quotaAndChurn.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.MARKETING_GROWTH}__${IndustryId.MEDIA_TELECOM}`]: {
            normal: [
                {
                    id: UseCaseId.SUBSCRIBER_CAC,
                    title: translate('sparkflow.useCase.marketingGrowth.mediaTelecom.subscriberCac.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.mediaTelecom.subscriberCac.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.mediaTelecom.subscriberCac.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.CONVERSION_FALL,
                    title: translate('sparkflow.useCase.marketingGrowth.mediaTelecom.conversionFall.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.mediaTelecom.conversionFall.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.mediaTelecom.conversionFall.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.ROAS_EROSION,
                    title: translate('sparkflow.useCase.marketingGrowth.mediaTelecom.roasErosion.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.mediaTelecom.roasErosion.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.mediaTelecom.roasErosion.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.CAC_BREAKDOWN,
                    title: translate('sparkflow.useCase.marketingGrowth.mediaTelecom.cacBreakdown.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.mediaTelecom.cacBreakdown.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.mediaTelecom.cacBreakdown.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.MQL_WASTE,
                    title: translate('sparkflow.useCase.marketingGrowth.mediaTelecom.mqlWaste.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.mediaTelecom.mqlWaste.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.mediaTelecom.mqlWaste.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.REGIONAL_ROAS_GAP,
                    title: translate('sparkflow.useCase.marketingGrowth.mediaTelecom.regionalRoasGap.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.mediaTelecom.regionalRoasGap.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.mediaTelecom.regionalRoasGap.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.PRODUCT_MANAGEMENT}__${IndustryId.MEDIA_TELECOM}`]: {
            normal: [
                {
                    id: UseCaseId.SUBSCRIBER_ADOPTION,
                    title: translate('sparkflow.useCase.productManagement.mediaTelecom.subscriberAdoption.title'),
                    description: translate('sparkflow.useCase.productManagement.mediaTelecom.subscriberAdoption.description'),
                    question: translate('sparkflow.useCase.productManagement.mediaTelecom.subscriberAdoption.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.SLOW_FEATURES,
                    title: translate('sparkflow.useCase.productManagement.mediaTelecom.slowFeatures.title'),
                    description: translate('sparkflow.useCase.productManagement.mediaTelecom.slowFeatures.description'),
                    question: translate('sparkflow.useCase.productManagement.mediaTelecom.slowFeatures.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.SUBSCRIBER_NPS,
                    title: translate('sparkflow.useCase.productManagement.mediaTelecom.subscriberNps.title'),
                    description: translate('sparkflow.useCase.productManagement.mediaTelecom.subscriberNps.description'),
                    question: translate('sparkflow.useCase.productManagement.mediaTelecom.subscriberNps.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.ADOPTION_COLLAPSE,
                    title: translate('sparkflow.useCase.productManagement.mediaTelecom.adoptionCollapse.title'),
                    description: translate('sparkflow.useCase.productManagement.mediaTelecom.adoptionCollapse.description'),
                    question: translate('sparkflow.useCase.productManagement.mediaTelecom.adoptionCollapse.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.RELEASE_BOTTLENECK,
                    title: translate('sparkflow.useCase.productManagement.mediaTelecom.releaseBottleneck.title'),
                    description: translate('sparkflow.useCase.productManagement.mediaTelecom.releaseBottleneck.description'),
                    question: translate('sparkflow.useCase.productManagement.mediaTelecom.releaseBottleneck.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.NPS_ADOPTION_RISK,
                    title: translate('sparkflow.useCase.productManagement.mediaTelecom.npsAdoptionRisk.title'),
                    description: translate('sparkflow.useCase.productManagement.mediaTelecom.npsAdoptionRisk.description'),
                    question: translate('sparkflow.useCase.productManagement.mediaTelecom.npsAdoptionRisk.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.FINANCE_ACCOUNTING}__${IndustryId.MEDIA_TELECOM}`]: {
            normal: [
                {
                    id: UseCaseId.ARPU_VS_COSTS,
                    title: translate('sparkflow.useCase.financeAccounting.mediaTelecom.arpuVsCosts.title'),
                    description: translate('sparkflow.useCase.financeAccounting.mediaTelecom.arpuVsCosts.description'),
                    question: translate('sparkflow.useCase.financeAccounting.mediaTelecom.arpuVsCosts.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DSO_RISK,
                    title: translate('sparkflow.useCase.financeAccounting.mediaTelecom.dsoRisk.title'),
                    description: translate('sparkflow.useCase.financeAccounting.mediaTelecom.dsoRisk.description'),
                    question: translate('sparkflow.useCase.financeAccounting.mediaTelecom.dsoRisk.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.BUDGET_OVERRUNS,
                    title: translate('sparkflow.useCase.financeAccounting.mediaTelecom.budgetOverruns.title'),
                    description: translate('sparkflow.useCase.financeAccounting.mediaTelecom.budgetOverruns.description'),
                    question: translate('sparkflow.useCase.financeAccounting.mediaTelecom.budgetOverruns.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.MARGIN_SQUEEZE,
                    title: translate('sparkflow.useCase.financeAccounting.mediaTelecom.marginSqueeze.title'),
                    description: translate('sparkflow.useCase.financeAccounting.mediaTelecom.marginSqueeze.description'),
                    question: translate('sparkflow.useCase.financeAccounting.mediaTelecom.marginSqueeze.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.AR_COLLECTIONS,
                    title: translate('sparkflow.useCase.financeAccounting.mediaTelecom.arCollections.title'),
                    description: translate('sparkflow.useCase.financeAccounting.mediaTelecom.arCollections.description'),
                    question: translate('sparkflow.useCase.financeAccounting.mediaTelecom.arCollections.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.EBITDA_EROSION,
                    title: translate('sparkflow.useCase.financeAccounting.mediaTelecom.ebitdaErosion.title'),
                    description: translate('sparkflow.useCase.financeAccounting.mediaTelecom.ebitdaErosion.description'),
                    question: translate('sparkflow.useCase.financeAccounting.mediaTelecom.ebitdaErosion.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.EXECUTIVE_LEADERSHIP}__${IndustryId.MEDIA_TELECOM}`]: {
            normal: [
                {
                    id: UseCaseId.REVENUE_VS_MARGIN,
                    title: translate('sparkflow.useCase.executiveLeadership.mediaTelecom.revenueVsMargin.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.mediaTelecom.revenueVsMargin.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.mediaTelecom.revenueVsMargin.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.SUBSCRIBER_SATISFACTION,
                    title: translate('sparkflow.useCase.executiveLeadership.mediaTelecom.subscriberSatisfaction.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.mediaTelecom.subscriberSatisfaction.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.mediaTelecom.subscriberSatisfaction.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.MARKET_SHARE,
                    title: translate('sparkflow.useCase.executiveLeadership.mediaTelecom.marketShare.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.mediaTelecom.marketShare.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.mediaTelecom.marketShare.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.PROFITABILITY_PARADOX,
                    title: translate('sparkflow.useCase.executiveLeadership.mediaTelecom.profitabilityParadox.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.mediaTelecom.profitabilityParadox.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.mediaTelecom.profitabilityParadox.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.SUBSCRIBER_RETENTION_RISK,
                    title: translate('sparkflow.useCase.executiveLeadership.mediaTelecom.subscriberRetentionRisk.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.mediaTelecom.subscriberRetentionRisk.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.mediaTelecom.subscriberRetentionRisk.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.OPEX_RETURNS,
                    title: translate('sparkflow.useCase.executiveLeadership.mediaTelecom.opexReturns.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.mediaTelecom.opexReturns.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.mediaTelecom.opexReturns.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.SOFTWARE_ENGINEERING}__${IndustryId.MEDIA_TELECOM}`]: {
            normal: [
                {
                    id: UseCaseId.BUILD_FAILURES,
                    title: translate('sparkflow.useCase.softwareEngineering.mediaTelecom.buildFailures.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.mediaTelecom.buildFailures.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.mediaTelecom.buildFailures.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DEPLOYMENT_LAG,
                    title: translate('sparkflow.useCase.softwareEngineering.mediaTelecom.deploymentLag.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.mediaTelecom.deploymentLag.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.mediaTelecom.deploymentLag.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.INCIDENT_RATE,
                    title: translate('sparkflow.useCase.softwareEngineering.mediaTelecom.incidentRate.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.mediaTelecom.incidentRate.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.mediaTelecom.incidentRate.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.BUILD_RELIABILITY,
                    title: translate('sparkflow.useCase.softwareEngineering.mediaTelecom.buildReliability.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.mediaTelecom.buildReliability.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.mediaTelecom.buildReliability.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DELIVERY_BOTTLENECK,
                    title: translate('sparkflow.useCase.softwareEngineering.mediaTelecom.deliveryBottleneck.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.mediaTelecom.deliveryBottleneck.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.mediaTelecom.deliveryBottleneck.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.PLATFORM_STABILITY,
                    title: translate('sparkflow.useCase.softwareEngineering.mediaTelecom.platformStability.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.mediaTelecom.platformStability.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.mediaTelecom.platformStability.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.DATA_ANALYTICS}__${IndustryId.MEDIA_TELECOM}`]: {
            normal: [
                {
                    id: UseCaseId.PIPELINE_FAILURES,
                    title: translate('sparkflow.useCase.dataAnalytics.mediaTelecom.pipelineFailures.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.mediaTelecom.pipelineFailures.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.mediaTelecom.pipelineFailures.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DATA_QUALITY,
                    title: translate('sparkflow.useCase.dataAnalytics.mediaTelecom.dataQuality.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.mediaTelecom.dataQuality.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.mediaTelecom.dataQuality.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.SLA_MISSES,
                    title: translate('sparkflow.useCase.dataAnalytics.mediaTelecom.slaMisses.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.mediaTelecom.slaMisses.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.mediaTelecom.slaMisses.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.FAILURE_ROOT_CAUSE,
                    title: translate('sparkflow.useCase.dataAnalytics.mediaTelecom.failureRootCause.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.mediaTelecom.failureRootCause.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.mediaTelecom.failureRootCause.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.LATENCY_QUALITY_GAP,
                    title: translate('sparkflow.useCase.dataAnalytics.mediaTelecom.latencyQualityGap.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.mediaTelecom.latencyQualityGap.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.mediaTelecom.latencyQualityGap.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.CAPACITY_RISK,
                    title: translate('sparkflow.useCase.dataAnalytics.mediaTelecom.capacityRisk.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.mediaTelecom.capacityRisk.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.mediaTelecom.capacityRisk.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.SALES_ACCOUNT_MANAGEMENT}__${IndustryId.RETAIL_CPG}`]: {
            normal: [
                {
                    id: UseCaseId.WIN_RATE_DROP,
                    title: translate('sparkflow.useCase.salesAccountManagement.retailCpg.winRateDrop.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.retailCpg.winRateDrop.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.retailCpg.winRateDrop.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.LONG_CLOSE_CYCLES,
                    title: translate('sparkflow.useCase.salesAccountManagement.retailCpg.longCloseCycles.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.retailCpg.longCloseCycles.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.retailCpg.longCloseCycles.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.RENEWAL_EROSION,
                    title: translate('sparkflow.useCase.salesAccountManagement.retailCpg.renewalErosion.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.retailCpg.renewalErosion.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.retailCpg.renewalErosion.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.WIN_RATE_COLLAPSE,
                    title: translate('sparkflow.useCase.salesAccountManagement.retailCpg.winRateCollapse.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.retailCpg.winRateCollapse.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.retailCpg.winRateCollapse.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.PIPELINE_STALL,
                    title: translate('sparkflow.useCase.salesAccountManagement.retailCpg.pipelineStall.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.retailCpg.pipelineStall.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.retailCpg.pipelineStall.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.QUOTA_AND_CHURN,
                    title: translate('sparkflow.useCase.salesAccountManagement.retailCpg.quotaAndChurn.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.retailCpg.quotaAndChurn.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.retailCpg.quotaAndChurn.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.MARKETING_GROWTH}__${IndustryId.RETAIL_CPG}`]: {
            normal: [
                {
                    id: UseCaseId.SHOPPER_CAC,
                    title: translate('sparkflow.useCase.marketingGrowth.retailCpg.shopperCac.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.retailCpg.shopperCac.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.retailCpg.shopperCac.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.CONVERSION_DROP,
                    title: translate('sparkflow.useCase.marketingGrowth.retailCpg.conversionDrop.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.retailCpg.conversionDrop.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.retailCpg.conversionDrop.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.ROAS_EROSION,
                    title: translate('sparkflow.useCase.marketingGrowth.retailCpg.roasErosion.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.retailCpg.roasErosion.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.retailCpg.roasErosion.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.CAC_BREAKDOWN,
                    title: translate('sparkflow.useCase.marketingGrowth.retailCpg.cacBreakdown.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.retailCpg.cacBreakdown.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.retailCpg.cacBreakdown.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.MQL_WASTE,
                    title: translate('sparkflow.useCase.marketingGrowth.retailCpg.mqlWaste.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.retailCpg.mqlWaste.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.retailCpg.mqlWaste.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.REGIONAL_ROAS_GAP,
                    title: translate('sparkflow.useCase.marketingGrowth.retailCpg.regionalRoasGap.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.retailCpg.regionalRoasGap.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.retailCpg.regionalRoasGap.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.PRODUCT_MANAGEMENT}__${IndustryId.RETAIL_CPG}`]: {
            normal: [
                {
                    id: UseCaseId.SHOPPER_ADOPTION,
                    title: translate('sparkflow.useCase.productManagement.retailCpg.shopperAdoption.title'),
                    description: translate('sparkflow.useCase.productManagement.retailCpg.shopperAdoption.description'),
                    question: translate('sparkflow.useCase.productManagement.retailCpg.shopperAdoption.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.SLOW_FEATURES,
                    title: translate('sparkflow.useCase.productManagement.retailCpg.slowFeatures.title'),
                    description: translate('sparkflow.useCase.productManagement.retailCpg.slowFeatures.description'),
                    question: translate('sparkflow.useCase.productManagement.retailCpg.slowFeatures.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.SHOPPER_NPS,
                    title: translate('sparkflow.useCase.productManagement.retailCpg.shopperNps.title'),
                    description: translate('sparkflow.useCase.productManagement.retailCpg.shopperNps.description'),
                    question: translate('sparkflow.useCase.productManagement.retailCpg.shopperNps.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.ADOPTION_COLLAPSE,
                    title: translate('sparkflow.useCase.productManagement.retailCpg.adoptionCollapse.title'),
                    description: translate('sparkflow.useCase.productManagement.retailCpg.adoptionCollapse.description'),
                    question: translate('sparkflow.useCase.productManagement.retailCpg.adoptionCollapse.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.RELEASE_BOTTLENECK,
                    title: translate('sparkflow.useCase.productManagement.retailCpg.releaseBottleneck.title'),
                    description: translate('sparkflow.useCase.productManagement.retailCpg.releaseBottleneck.description'),
                    question: translate('sparkflow.useCase.productManagement.retailCpg.releaseBottleneck.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.NPS_ADOPTION_RISK,
                    title: translate('sparkflow.useCase.productManagement.retailCpg.npsAdoptionRisk.title'),
                    description: translate('sparkflow.useCase.productManagement.retailCpg.npsAdoptionRisk.description'),
                    question: translate('sparkflow.useCase.productManagement.retailCpg.npsAdoptionRisk.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.FINANCE_ACCOUNTING}__${IndustryId.RETAIL_CPG}`]: {
            normal: [
                {
                    id: UseCaseId.MARGIN_SQUEEZE,
                    title: translate('sparkflow.useCase.financeAccounting.retailCpg.marginSqueeze.title'),
                    description: translate('sparkflow.useCase.financeAccounting.retailCpg.marginSqueeze.description'),
                    question: translate('sparkflow.useCase.financeAccounting.retailCpg.marginSqueeze.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DSO_RISK,
                    title: translate('sparkflow.useCase.financeAccounting.retailCpg.dsoRisk.title'),
                    description: translate('sparkflow.useCase.financeAccounting.retailCpg.dsoRisk.description'),
                    question: translate('sparkflow.useCase.financeAccounting.retailCpg.dsoRisk.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.BUDGET_OVERRUNS,
                    title: translate('sparkflow.useCase.financeAccounting.retailCpg.budgetOverruns.title'),
                    description: translate('sparkflow.useCase.financeAccounting.retailCpg.budgetOverruns.description'),
                    question: translate('sparkflow.useCase.financeAccounting.retailCpg.budgetOverruns.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.REVENUE_COST_GAP,
                    title: translate('sparkflow.useCase.financeAccounting.retailCpg.revenueCostGap.title'),
                    description: translate('sparkflow.useCase.financeAccounting.retailCpg.revenueCostGap.description'),
                    question: translate('sparkflow.useCase.financeAccounting.retailCpg.revenueCostGap.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.AR_COLLECTIONS_RISK,
                    title: translate('sparkflow.useCase.financeAccounting.retailCpg.arCollectionsRisk.title'),
                    description: translate('sparkflow.useCase.financeAccounting.retailCpg.arCollectionsRisk.description'),
                    question: translate('sparkflow.useCase.financeAccounting.retailCpg.arCollectionsRisk.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.EBITDA_EROSION,
                    title: translate('sparkflow.useCase.financeAccounting.retailCpg.ebitdaErosion.title'),
                    description: translate('sparkflow.useCase.financeAccounting.retailCpg.ebitdaErosion.description'),
                    question: translate('sparkflow.useCase.financeAccounting.retailCpg.ebitdaErosion.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.EXECUTIVE_LEADERSHIP}__${IndustryId.RETAIL_CPG}`]: {
            normal: [
                {
                    id: UseCaseId.REVENUE_VS_MARGIN,
                    title: translate('sparkflow.useCase.executiveLeadership.retailCpg.revenueVsMargin.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.retailCpg.revenueVsMargin.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.retailCpg.revenueVsMargin.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.SHOPPER_SATISFACTION,
                    title: translate('sparkflow.useCase.executiveLeadership.retailCpg.shopperSatisfaction.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.retailCpg.shopperSatisfaction.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.retailCpg.shopperSatisfaction.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.MARKET_SHARE,
                    title: translate('sparkflow.useCase.executiveLeadership.retailCpg.marketShare.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.retailCpg.marketShare.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.retailCpg.marketShare.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.PROFITABILITY_GAP,
                    title: translate('sparkflow.useCase.executiveLeadership.retailCpg.profitabilityGap.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.retailCpg.profitabilityGap.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.retailCpg.profitabilityGap.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.SHOPPER_RETENTION_RISK,
                    title: translate('sparkflow.useCase.executiveLeadership.retailCpg.shopperRetentionRisk.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.retailCpg.shopperRetentionRisk.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.retailCpg.shopperRetentionRisk.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.OPEX_RETURNS,
                    title: translate('sparkflow.useCase.executiveLeadership.retailCpg.opexReturns.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.retailCpg.opexReturns.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.retailCpg.opexReturns.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.SOFTWARE_ENGINEERING}__${IndustryId.RETAIL_CPG}`]: {
            normal: [
                {
                    id: UseCaseId.BUILD_FAILURES,
                    title: translate('sparkflow.useCase.softwareEngineering.retailCpg.buildFailures.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.retailCpg.buildFailures.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.retailCpg.buildFailures.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DEPLOYMENT_LAG,
                    title: translate('sparkflow.useCase.softwareEngineering.retailCpg.deploymentLag.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.retailCpg.deploymentLag.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.retailCpg.deploymentLag.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.INCIDENT_RATE,
                    title: translate('sparkflow.useCase.softwareEngineering.retailCpg.incidentRate.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.retailCpg.incidentRate.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.retailCpg.incidentRate.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.BUILD_RELIABILITY,
                    title: translate('sparkflow.useCase.softwareEngineering.retailCpg.buildReliability.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.retailCpg.buildReliability.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.retailCpg.buildReliability.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DELIVERY_BOTTLENECK,
                    title: translate('sparkflow.useCase.softwareEngineering.retailCpg.deliveryBottleneck.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.retailCpg.deliveryBottleneck.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.retailCpg.deliveryBottleneck.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.PLATFORM_STABILITY,
                    title: translate('sparkflow.useCase.softwareEngineering.retailCpg.platformStability.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.retailCpg.platformStability.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.retailCpg.platformStability.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.DATA_ANALYTICS}__${IndustryId.RETAIL_CPG}`]: {
            normal: [
                {
                    id: UseCaseId.PIPELINE_FAILURES,
                    title: translate('sparkflow.useCase.dataAnalytics.retailCpg.pipelineFailures.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.retailCpg.pipelineFailures.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.retailCpg.pipelineFailures.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DATA_QUALITY,
                    title: translate('sparkflow.useCase.dataAnalytics.retailCpg.dataQuality.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.retailCpg.dataQuality.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.retailCpg.dataQuality.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.SLA_MISSES,
                    title: translate('sparkflow.useCase.dataAnalytics.retailCpg.slaMisses.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.retailCpg.slaMisses.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.retailCpg.slaMisses.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.FAILURE_ROOT_CAUSE,
                    title: translate('sparkflow.useCase.dataAnalytics.retailCpg.failureRootCause.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.retailCpg.failureRootCause.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.retailCpg.failureRootCause.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.LATENCY_QUALITY_GAP,
                    title: translate('sparkflow.useCase.dataAnalytics.retailCpg.latencyQualityGap.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.retailCpg.latencyQualityGap.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.retailCpg.latencyQualityGap.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.CAPACITY_RISK,
                    title: translate('sparkflow.useCase.dataAnalytics.retailCpg.capacityRisk.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.retailCpg.capacityRisk.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.retailCpg.capacityRisk.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.SALES_ACCOUNT_MANAGEMENT}__${IndustryId.SOFTWARE_TECHNOLOGY}`]: {
            normal: [
                {
                    id: UseCaseId.WIN_RATE_DROP,
                    title: translate('sparkflow.useCase.salesAccountManagement.softwareTechnology.winRateDrop.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.softwareTechnology.winRateDrop.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.softwareTechnology.winRateDrop.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.CAC_PAYBACK_DRAG,
                    title: translate('sparkflow.useCase.salesAccountManagement.softwareTechnology.cacPaybackDrag.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.softwareTechnology.cacPaybackDrag.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.softwareTechnology.cacPaybackDrag.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.RENEWAL_RISK,
                    title: translate('sparkflow.useCase.salesAccountManagement.softwareTechnology.renewalRisk.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.softwareTechnology.renewalRisk.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.softwareTechnology.renewalRisk.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.WIN_RATE_COLLAPSE,
                    title: translate('sparkflow.useCase.salesAccountManagement.softwareTechnology.winRateCollapse.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.softwareTechnology.winRateCollapse.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.softwareTechnology.winRateCollapse.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.PIPELINE_LEAK,
                    title: translate('sparkflow.useCase.salesAccountManagement.softwareTechnology.pipelineLeak.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.softwareTechnology.pipelineLeak.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.softwareTechnology.pipelineLeak.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.QUOTA_AND_CHURN,
                    title: translate('sparkflow.useCase.salesAccountManagement.softwareTechnology.quotaAndChurn.title'),
                    description: translate('sparkflow.useCase.salesAccountManagement.softwareTechnology.quotaAndChurn.description'),
                    question: translate('sparkflow.useCase.salesAccountManagement.softwareTechnology.quotaAndChurn.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.MARKETING_GROWTH}__${IndustryId.SOFTWARE_TECHNOLOGY}`]: {
            normal: [
                {
                    id: UseCaseId.CAC_RISING,
                    title: translate('sparkflow.useCase.marketingGrowth.softwareTechnology.cacRising.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.softwareTechnology.cacRising.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.softwareTechnology.cacRising.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.MQL_CONVERSION,
                    title: translate('sparkflow.useCase.marketingGrowth.softwareTechnology.mqlConversion.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.softwareTechnology.mqlConversion.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.softwareTechnology.mqlConversion.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.ROAS_EROSION,
                    title: translate('sparkflow.useCase.marketingGrowth.softwareTechnology.roasErosion.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.softwareTechnology.roasErosion.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.softwareTechnology.roasErosion.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.CAC_BREAKDOWN,
                    title: translate('sparkflow.useCase.marketingGrowth.softwareTechnology.cacBreakdown.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.softwareTechnology.cacBreakdown.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.softwareTechnology.cacBreakdown.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.MQL_TO_SQL_GAP,
                    title: translate('sparkflow.useCase.marketingGrowth.softwareTechnology.mqlToSqlGap.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.softwareTechnology.mqlToSqlGap.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.softwareTechnology.mqlToSqlGap.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.REGIONAL_PIPELINE_GAP,
                    title: translate('sparkflow.useCase.marketingGrowth.softwareTechnology.regionalPipelineGap.title'),
                    description: translate('sparkflow.useCase.marketingGrowth.softwareTechnology.regionalPipelineGap.description'),
                    question: translate('sparkflow.useCase.marketingGrowth.softwareTechnology.regionalPipelineGap.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.PRODUCT_MANAGEMENT}__${IndustryId.SOFTWARE_TECHNOLOGY}`]: {
            normal: [
                {
                    id: UseCaseId.FEATURE_ADOPTION,
                    title: translate('sparkflow.useCase.productManagement.softwareTechnology.featureAdoption.title'),
                    description: translate('sparkflow.useCase.productManagement.softwareTechnology.featureAdoption.description'),
                    question: translate('sparkflow.useCase.productManagement.softwareTechnology.featureAdoption.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.SLOW_TO_SHIP,
                    title: translate('sparkflow.useCase.productManagement.softwareTechnology.slowToShip.title'),
                    description: translate('sparkflow.useCase.productManagement.softwareTechnology.slowToShip.description'),
                    question: translate('sparkflow.useCase.productManagement.softwareTechnology.slowToShip.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.NPS_SLIPPAGE,
                    title: translate('sparkflow.useCase.productManagement.softwareTechnology.npsSlippage.title'),
                    description: translate('sparkflow.useCase.productManagement.softwareTechnology.npsSlippage.description'),
                    question: translate('sparkflow.useCase.productManagement.softwareTechnology.npsSlippage.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.PLG_ADOPTION_GAP,
                    title: translate('sparkflow.useCase.productManagement.softwareTechnology.plgAdoptionGap.title'),
                    description: translate('sparkflow.useCase.productManagement.softwareTechnology.plgAdoptionGap.description'),
                    question: translate('sparkflow.useCase.productManagement.softwareTechnology.plgAdoptionGap.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.RELEASE_BOTTLENECK,
                    title: translate('sparkflow.useCase.productManagement.softwareTechnology.releaseBottleneck.title'),
                    description: translate('sparkflow.useCase.productManagement.softwareTechnology.releaseBottleneck.description'),
                    question: translate('sparkflow.useCase.productManagement.softwareTechnology.releaseBottleneck.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.NPS_ADOPTION_RISK,
                    title: translate('sparkflow.useCase.productManagement.softwareTechnology.npsAdoptionRisk.title'),
                    description: translate('sparkflow.useCase.productManagement.softwareTechnology.npsAdoptionRisk.description'),
                    question: translate('sparkflow.useCase.productManagement.softwareTechnology.npsAdoptionRisk.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.FINANCE_ACCOUNTING}__${IndustryId.SOFTWARE_TECHNOLOGY}`]: {
            normal: [
                {
                    id: UseCaseId.MARGIN_SQUEEZE,
                    title: translate('sparkflow.useCase.financeAccounting.softwareTechnology.marginSqueeze.title'),
                    description: translate('sparkflow.useCase.financeAccounting.softwareTechnology.marginSqueeze.description'),
                    question: translate('sparkflow.useCase.financeAccounting.softwareTechnology.marginSqueeze.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DSO_RISK,
                    title: translate('sparkflow.useCase.financeAccounting.softwareTechnology.dsoRisk.title'),
                    description: translate('sparkflow.useCase.financeAccounting.softwareTechnology.dsoRisk.description'),
                    question: translate('sparkflow.useCase.financeAccounting.softwareTechnology.dsoRisk.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.BUDGET_OVERRUNS,
                    title: translate('sparkflow.useCase.financeAccounting.softwareTechnology.budgetOverruns.title'),
                    description: translate('sparkflow.useCase.financeAccounting.softwareTechnology.budgetOverruns.description'),
                    question: translate('sparkflow.useCase.financeAccounting.softwareTechnology.budgetOverruns.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.REVENUE_COST_GAP,
                    title: translate('sparkflow.useCase.financeAccounting.softwareTechnology.revenueCostGap.title'),
                    description: translate('sparkflow.useCase.financeAccounting.softwareTechnology.revenueCostGap.description'),
                    question: translate('sparkflow.useCase.financeAccounting.softwareTechnology.revenueCostGap.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.AR_COLLECTIONS_RISK,
                    title: translate('sparkflow.useCase.financeAccounting.softwareTechnology.arCollectionsRisk.title'),
                    description: translate('sparkflow.useCase.financeAccounting.softwareTechnology.arCollectionsRisk.description'),
                    question: translate('sparkflow.useCase.financeAccounting.softwareTechnology.arCollectionsRisk.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.EBITDA_EROSION,
                    title: translate('sparkflow.useCase.financeAccounting.softwareTechnology.ebitdaErosion.title'),
                    description: translate('sparkflow.useCase.financeAccounting.softwareTechnology.ebitdaErosion.description'),
                    question: translate('sparkflow.useCase.financeAccounting.softwareTechnology.ebitdaErosion.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.EXECUTIVE_LEADERSHIP}__${IndustryId.SOFTWARE_TECHNOLOGY}`]: {
            normal: [
                {
                    id: UseCaseId.ARR_VS_MARGIN,
                    title: translate('sparkflow.useCase.executiveLeadership.softwareTechnology.arrVsMargin.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.softwareTechnology.arrVsMargin.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.softwareTechnology.arrVsMargin.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.CUSTOMER_NPS_DROP,
                    title: translate('sparkflow.useCase.executiveLeadership.softwareTechnology.customerNpsDrop.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.softwareTechnology.customerNpsDrop.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.softwareTechnology.customerNpsDrop.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.MARKET_SHARE,
                    title: translate('sparkflow.useCase.executiveLeadership.softwareTechnology.marketShare.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.softwareTechnology.marketShare.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.softwareTechnology.marketShare.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.GROWTH_MARGIN_TRADE,
                    title: translate('sparkflow.useCase.executiveLeadership.softwareTechnology.growthMarginTrade.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.softwareTechnology.growthMarginTrade.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.softwareTechnology.growthMarginTrade.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.CUSTOMER_RETENTION_RISK,
                    title: translate('sparkflow.useCase.executiveLeadership.softwareTechnology.customerRetentionRisk.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.softwareTechnology.customerRetentionRisk.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.softwareTechnology.customerRetentionRisk.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.OPEX_EFFICIENCY,
                    title: translate('sparkflow.useCase.executiveLeadership.softwareTechnology.opexEfficiency.title'),
                    description: translate('sparkflow.useCase.executiveLeadership.softwareTechnology.opexEfficiency.description'),
                    question: translate('sparkflow.useCase.executiveLeadership.softwareTechnology.opexEfficiency.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.SOFTWARE_ENGINEERING}__${IndustryId.SOFTWARE_TECHNOLOGY}`]: {
            normal: [
                {
                    id: UseCaseId.BUILD_RELIABILITY,
                    title: translate('sparkflow.useCase.softwareEngineering.softwareTechnology.buildReliability.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.softwareTechnology.buildReliability.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.softwareTechnology.buildReliability.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.SLOW_DEPLOYMENTS,
                    title: translate('sparkflow.useCase.softwareEngineering.softwareTechnology.slowDeployments.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.softwareTechnology.slowDeployments.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.softwareTechnology.slowDeployments.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.INCIDENT_SURGE,
                    title: translate('sparkflow.useCase.softwareEngineering.softwareTechnology.incidentSurge.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.softwareTechnology.incidentSurge.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.softwareTechnology.incidentSurge.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.BUILD_FAILURE_CAUSE,
                    title: translate('sparkflow.useCase.softwareEngineering.softwareTechnology.buildFailureCause.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.softwareTechnology.buildFailureCause.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.softwareTechnology.buildFailureCause.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DORA_METRIC_GAP,
                    title: translate('sparkflow.useCase.softwareEngineering.softwareTechnology.doraMetricGap.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.softwareTechnology.doraMetricGap.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.softwareTechnology.doraMetricGap.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.PLATFORM_STABILITY,
                    title: translate('sparkflow.useCase.softwareEngineering.softwareTechnology.platformStability.title'),
                    description: translate('sparkflow.useCase.softwareEngineering.softwareTechnology.platformStability.description'),
                    question: translate('sparkflow.useCase.softwareEngineering.softwareTechnology.platformStability.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
        [`${FunctionId.DATA_ANALYTICS}__${IndustryId.SOFTWARE_TECHNOLOGY}`]: {
            normal: [
                {
                    id: UseCaseId.PIPELINE_FAILURES,
                    title: translate('sparkflow.useCase.dataAnalytics.softwareTechnology.pipelineFailures.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.softwareTechnology.pipelineFailures.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.softwareTechnology.pipelineFailures.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.DATA_QUALITY_DROP,
                    title: translate('sparkflow.useCase.dataAnalytics.softwareTechnology.dataQualityDrop.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.softwareTechnology.dataQualityDrop.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.softwareTechnology.dataQualityDrop.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.SLA_MISSES,
                    title: translate('sparkflow.useCase.dataAnalytics.softwareTechnology.slaMisses.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.softwareTechnology.slaMisses.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.softwareTechnology.slaMisses.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
            deepResearch: [
                {
                    id: UseCaseId.FAILURE_ROOT_CAUSE,
                    title: translate('sparkflow.useCase.dataAnalytics.softwareTechnology.failureRootCause.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.softwareTechnology.failureRootCause.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.softwareTechnology.failureRootCause.question'),
                    icon: IconID.CHART_V2,
                },
                {
                    id: UseCaseId.LATENCY_QUALITY_GAP,
                    title: translate('sparkflow.useCase.dataAnalytics.softwareTechnology.latencyQualityGap.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.softwareTechnology.latencyQualityGap.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.softwareTechnology.latencyQualityGap.question'),
                    icon: IconID.CREATE_WORKSHEET_V2,
                },
                {
                    id: UseCaseId.CAPACITY_RISK,
                    title: translate('sparkflow.useCase.dataAnalytics.softwareTechnology.capacityRisk.title'),
                    description: translate('sparkflow.useCase.dataAnalytics.softwareTechnology.capacityRisk.description'),
                    question: translate('sparkflow.useCase.dataAnalytics.softwareTechnology.capacityRisk.question'),
                    icon: IconID.ANSWER_V3,
                },
            ],
        },
    };
    const key = `${functionId}__${industryId}`;
    const data = USE_CASE_QUESTIONS_MAP[key];
    if (!data)
        return [];
    return isDeepResearch ? data.deepResearch : data.normal;
};
const rootUrl = window.location.origin;
export const getSparkflowBaseUrl = () => getSessionInfo()?.configInfo?.sparkFlowConfig?.spotBuildHost;
export const getSparkflowChatEndpoint = (isMTService) => isMTService
    ? `${rootUrl}/onboarding/generate-objects`
    : `${getSparkflowBaseUrl()}/api/v1/apps/chat`;
export const getSSEStreamUrl = (isMTService) => isMTService
    ? `${rootUrl}/onboarding/sse/stream`
    : `${getSparkflowBaseUrl()}/api/v1/sse/stream`;
export const BUILD_PHASE = {
    INITIALIZING: 'initialising',
    BUILDING: 'build-process',
    GENERATING_PREVIEW: 'generating-preview',
    PREVIEW_READY: 'preview-ready',
    COMPLETED: 'build-complete',
};
export const SPARKFLOW_TEST_IDS = {
    FORM_V1: 'sparkflow-form-v1',
    FORM_V2: 'sparkflow-form-v2',
    CONTINUE_BUTTON: 'sparkflow-stepone-continue-button',
    MILESTONE_MODAL: 'sparkflow-milestone-modal',
    CHANGE_INFO: 'sparkflow-change-info',
    BUILD_COMPLETE_CTA: 'sparkflow-build-complete-cta',
};
export const MILESTONE_CHECKMARK_SRC = '/node_modules/@thoughtspot/radiant-styles/public/img/sparkflow/sparkflow-milestone-checkmark.svg';
export const getMilestoneConfig = (agentName, status) => {
    const config = {
        [MilestoneAgentName.SPOTTER_CHAT]: {
            [MilestoneStatus.STARTED]: {
                title: translate('sparkflow.spotter.milestone.started.title'),
                subtitle: translate('sparkflow.spotter.milestone.started.subtitle'),
                primaryCta: translate('sparkflow.spotter.milestone.started.primary.cta'),
                bgImageClassName: 'spotterStartedImage',
            },
            [MilestoneStatus.FINISHED]: {
                title: translate('sparkflow.spotter.milestone.finished.title'),
                subtitle: translate('sparkflow.spotter.milestone.finished.subtitle'),
                primaryCta: translate('sparkflow.spotter.milestone.finished.primary.cta'),
                secondaryCta: translate('sparkflow.spotter.milestone.finished.secondary.cta'),
                tertiaryCta: translate('sparkflow.spotter.milestone.finished.tertiary.cta'),
                bgImageClassName: 'spotterFinishedImage',
            },
        },
        [MilestoneAgentName.SPOTTER_VIZ]: {
            [MilestoneStatus.STARTED]: {
                title: translate('sparkflow.spotterViz.milestone.started.title'),
                subtitle: translate('sparkflow.spotterViz.milestone.started.subtitle'),
                primaryCta: translate('sparkflow.spotterViz.milestone.started.primary.cta'),
                bgImageClassName: 'spotterVizStartedImage',
            },
            [MilestoneStatus.FINISHED]: {
                title: translate('sparkflow.spotterViz.milestone.finished.title'),
                subtitle: translate('sparkflow.spotterViz.milestone.finished.subtitle'),
                primaryCta: translate('sparkflow.spotterViz.milestone.finished.primary.cta'),
                secondaryCta: translate('sparkflow.spotterViz.milestone.finished.secondary.cta'),
                tertiaryCta: translate('sparkflow.spotterViz.milestone.finished.tertiary.cta'),
                bgImageClassName: 'spotterVizFinishedImage',
            },
        },
        [MilestoneAgentName.SPOTTER_CODE]: {
            [MilestoneStatus.STARTED]: {
                title: translate('sparkflow.spotterCode.milestone.started.title'),
                subtitle: translate('sparkflow.spotterCode.milestone.started.subtitle'),
                primaryCta: translate('sparkflow.spotterCode.milestone.started.primary.cta'),
                secondaryCta: translate('sparkflow.spotterCode.milestone.started.secondary.cta'),
                bgImageClassName: 'spotterCodeStartedImage',
            },
            [MilestoneStatus.FINISHED]: {
                title: translate('sparkflow.spotterCode.milestone.finished.title'),
                subtitle: translate('sparkflow.spotterCode.milestone.finished.subtitle'),
                primaryCta: translate('sparkflow.spotterCode.milestone.finished.primary.cta'),
                secondaryCta: translate('sparkflow.spotterCode.milestone.finished.secondary.cta'),
                tertiaryCta: translate('sparkflow.spotterCode.milestone.finished.tertiary.cta'),
                bgImageClassName: 'spotterCodeFinishedImage',
            },
        },
    };
    return config[agentName]?.[status] ?? null;
};
export const SaveAgentIdMapping = {
    [AgentId.ASK_QUESTIONS]: 'SPOTTER_AGENT_PAGE',
    [AgentId.BUILD_DASHBOARD]: 'SPOTTER_VIZ_AGENT_PAGE',
    [AgentId.EMBED_INSIGHTS]: 'SPOTTER_CODE_AGENT_PAGE',
};
export const SaveIdToAgentIdMapping = {
    SPOTTER_AGENT_PAGE: AgentId.ASK_QUESTIONS,
    SPOTTER_VIZ_AGENT_PAGE: AgentId.BUILD_DASHBOARD,
    SPOTTER_CODE_AGENT_PAGE: AgentId.EMBED_INSIGHTS,
};
//# sourceMappingURL=sparkflow-constants.js.map